import moment from 'moment-timezone'

import { MS, SECONDS } from 'utils/millisecond'

export const MOMENT_FORMAT_TIME_PERIOD_DATE = 'YYYY-MM-DD'
export const MOMENT_FORMAT_DATE = 'M/D/YYYY'
export const MOMENT_FORMAT_TIME = 'h:mm A'
export const MOMENT_FORMAT_DATE_TIME = 'M/D/YYYY h:mm A'
export const MOMENT_FORMAT_DAY = 'ddd'
export const MOMENT_FORMAT_TIMESTAMP = 'X'

export enum TimePeriodLabels {
  Weekly = 'weekly',
  Biweekly = 'biweekly',
  Monthly = 'monthly',
  'Months3' = '3 months',
  'Months6' = '6 months',
  'Months12' = '12 months'
}

export function formatTimePeriodDate(date?: moment.MomentInput) {
  return moment(date).format(MOMENT_FORMAT_TIME_PERIOD_DATE)
}

const TIME_PERIOD_TABLE = {
  [TimePeriodLabels.Weekly]: {
    label: '1 week',
    period: 'DAYS',
    start: formatTimePeriodDate(moment().subtract(7, 'days')),
    end: formatTimePeriodDate()
  },
  [TimePeriodLabels.Biweekly]: {
    label: '2 weeks',
    period: 'DAYS',
    start: formatTimePeriodDate(moment().subtract(14, 'days')),
    end: formatTimePeriodDate()
  },
  [TimePeriodLabels.Monthly]: {
    label: '1 month',
    period: 'WEEKS',
    start: formatTimePeriodDate(moment().subtract(1, 'months')),
    end: formatTimePeriodDate()
  },
  [TimePeriodLabels.Months3]: {
    label: '3 months',
    period: 'WEEKS',
    start: formatTimePeriodDate(moment().subtract(3, 'months')),
    end: formatTimePeriodDate()
  },
  [TimePeriodLabels.Months6]: {
    label: '6 months',
    period: 'MONTHS',
    start: formatTimePeriodDate(moment().subtract(6, 'months')),
    end: formatTimePeriodDate()
  },
  [TimePeriodLabels.Months12]: {
    label: '12 months',
    period: 'MONTHS',
    start: formatTimePeriodDate(moment().subtract(1, 'years')),
    end: formatTimePeriodDate()
  }
}

export function formatDate(date?: moment.MomentInput) {
  return moment(date).format(MOMENT_FORMAT_DATE)
}

export function formatDay(date?: moment.MomentInput) {
  return moment(date).format(MOMENT_FORMAT_DAY)
}

export function formatJSDate(date?: moment.MomentInput) {
  return moment(date).toDate()
}

export function formatTime(date?: moment.MomentInput) {
  return moment(date).format(MOMENT_FORMAT_TIME)
}

export function formatTimestamp(date?: moment.MomentInput) {
  return moment(date).format(MOMENT_FORMAT_TIMESTAMP)
}

export function formatDateTime(
  date?: moment.MomentInput,
  options?: { showLocalTimeZone: boolean }
) {
  const { showLocalTimeZone = false } = options ?? {}

  if (showLocalTimeZone) {
    const tz = moment.tz.guess()
    return moment(date).tz(tz).format(`${MOMENT_FORMAT_DATE_TIME} z`)
  }

  return moment(date).format(MOMENT_FORMAT_DATE_TIME)
}

export function reportsTimePeriod({
  duration
}: {
  duration: TimePeriodLabels
}) {
  return TIME_PERIOD_TABLE[duration] || null
}

export function getRelativeTimeFromNow(date: Date) {
  // NOTE: Given that we aren't i18l outside of US, we can bake that in here as 'en-us'
  const formatter = new Intl.RelativeTimeFormat('en-us', { numeric: 'auto' })
  const diffInSeconds = (date.getTime() - Date.now()) / MS.seconds

  if (diffInSeconds === 0) return 'Now'

  for (const unit in SECONDS) {
    if (Math.abs(diffInSeconds) >= SECONDS[unit]) {
      const value = Math.round(diffInSeconds / SECONDS[unit])

      return formatter.format(value, unit as Intl.RelativeTimeFormatUnit)
    }
  }
}
